import type { Expense, ExpenseCategory, ExpenseInput } from '../../types/app'
import { parsePrice, priceInput } from '../../catalog/format'

// EXPENSE_CATEGORIES drives the expense sheet's category chips, in the order
// they're offered.
export const EXPENSE_CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'rent', label: 'Rent' },
  { value: 'products', label: 'Products' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
]

export const CATEGORY_LABELS: Record<ExpenseCategory, string> = EXPENSE_CATEGORIES.reduce(
  (labels, category) => ({ ...labels, [category.value]: category.label }),
  {} as Record<ExpenseCategory, string>,
)

export interface ExpenseForm {
  amount: string
  category: ExpenseCategory | ''
  spent_on: string
  note: string
}

export function toExpenseForm(expense: Expense | null, today: string): ExpenseForm {
  return {
    amount: expense ? priceInput(expense.amount_pence) : '',
    category: expense?.category ?? '',
    spent_on: expense?.spent_on ?? today,
    note: expense?.note ?? '',
  }
}

// toExpenseInput checks the same rules the API does (internal/catalog/expense.go),
// with the same messages, so a mistake is caught before the request and the
// API's own 422 field errors, if any slip through, land on the same fields.
export function toExpenseInput(form: ExpenseForm, today: string): { input: ExpenseInput } | { errors: Record<string, string> } {
  const errors: Record<string, string> = {}

  const amountPence = parsePrice(form.amount)
  if (amountPence === null || amountPence < 1 || amountPence > 10_000_000) {
    errors.amount_pence = 'Enter an amount between 1p and £100,000.'
  }

  if (form.category === '') {
    errors.category = 'Choose a category.'
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.spent_on)) {
    errors.spent_on = 'Enter a valid date.'
  } else if (form.spent_on > today) {
    errors.spent_on = 'Choose today or an earlier date.'
  }

  const note = form.note.trim()
  if (note.length > 200) {
    errors.note = 'Keep the note to 200 characters or fewer.'
  }

  if (Object.keys(errors).length > 0 || amountPence === null || form.category === '') {
    return { errors }
  }

  return {
    input: {
      spent_on: form.spent_on,
      amount_pence: amountPence,
      category: form.category,
      note: note === '' ? null : note,
    },
  }
}
